import { useContext, useEffect } from 'react'
import { classes, style } from 'typestyle'
import config from '../../config'
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Checkbox } from '../../__aeki__/components/checkbox/checkbox.component'
import { Line } from '../../__aeki__/components/line'
import { Select } from '../../__aeki__/components/select'
import { Heading } from '../../__aeki__/components/text/heading.component'
import { Text } from '../../__aeki__/components/text/text.component'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'
import { withRouter } from '../../__aeki__/contexts/with-router'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'

/* Components =================================================================================== */
import { Snippet, SnippetHead, SnippetBody } from '../../__aeki__/modules/layouts/snippet'
import { f18i } from '../../__aeki__/styles/styleset/font-size/f18i'
import { ml12 } from '../../__aeki__/styles/styleset/margin/ml12'
import { ml16 } from '../../__aeki__/styles/styleset/margin/ml16'
import { ml8 } from '../../__aeki__/styles/styleset/margin/ml8'
import { mr12 } from '../../__aeki__/styles/styleset/margin/mr12'
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'
import { mr8 } from '../../__aeki__/styles/styleset/margin/mr8'

/* Styles ======================================================================================= */
import { mt8 } from '../../__aeki__/styles/styleset/margin/mt8'
import { pb12 } from '../../__aeki__/styles/styleset/padding/pb12'
import { pb4 } from '../../__aeki__/styles/styleset/padding/pb4'
import { pb8 } from '../../__aeki__/styles/styleset/padding/pb8'
import { pl12md } from '../../__aeki__/styles/styleset/padding/pl12md'
import { pl16 } from '../../__aeki__/styles/styleset/padding/pl16'
import { pr12md } from '../../__aeki__/styles/styleset/padding/pr12md'
import { pr16 } from '../../__aeki__/styles/styleset/padding/pr16'
import { pt12 } from '../../__aeki__/styles/styleset/padding/pt12'
import { pt16 } from '../../__aeki__/styles/styleset/padding/pt16'
import { pt4 } from '../../__aeki__/styles/styleset/padding/pt4'
import { pt8 } from '../../__aeki__/styles/styleset/padding/pt8'

const COLOR_SCHEMES = [
  {
    primary: config.theme[0].baselines.primary.color.dark,
    secondary: config.theme[0].baselines.primary.color.medium,
  },
  {
    primary: '#f9a349',
    secondary: '#ffce16',
  },
]

export default addSnippet(
  'Analytics',
  withRouter((props: any) => {
    const { title, params = {} } = props
    const { url, colorScheme = 0 } = params
    const { groupId } = props?.match.params
    const { navigation, layout, schema } = useContext(NavigationContext)

    const [data, $data] = useFetch(
      {
        page: 0,
        labels: [],
        data: [],
        options: [],
      },
      { url: url.replace('{{groupId}}', groupId), search: { page: 0 } },
    )
    const [toggled, $toggled] = useValue({})
    const [option, $option] = useValue('')

    const { payload } = data as any

    useEffect(() => {
      if (data.mounted) {
        $data.get().then((payload: any) => {
          const toggled: any = {}

          payload.data.map((item: any) => {
            toggled[item.label] = true
          })

          $option.set(payload?.options?.[0])

          $toggled.set(toggled)
        })
      }
    }, [data.mounted])

    const height = 180
    const bottomY = (7 / 8) * height + 0.5
    const bottomEndY = (8 / 8) * height + 0.5

    return (
      <Snippet>
        <Box
          baseline="none"
          className={classes(pl12md, pr12md, pl16, pr16)}
          minHeight={48}
          maxHeight={48}
          spaceBetween
          alignCenterY
          flex
        >
          {title && (
            <Box baseline="surface" flex alignCenterY>
              <Box baseline="surface" flex alignCenterY>
                <Text size="xl">{title}</Text>
                <Box className={style({ height: 20 })} flex>
                  <Line horiz dark className={classes(ml12, mr12)} />
                </Box>
                <Box className={ml8}>
                  <Select
                    value={option}
                    options={payload?.options}
                    onChange={(value: any) => {
                      $option.set(value)
                      $data.get({ search: { option: value } })
                    }}
                    size="sm"
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box baseline="none" flex alignCenterY>
            <Box className={style({ height: 20 })} flex>
              <Text
                size="sm"
                style={{
                  marginTop: 3.5,
                  color: 'rgb(120,120,120)',
                }}
                className={mr8}
              >
                {payload.paginationText}
              </Text>
            </Box>
            {
              <Button
                // baseline={today.toDateString() == refDate.toDateString() ? 'disabled' : 'surface'}
                className={mr4}
                size="sm"
                icon={<i className={classes('material-icons', f18i)}>keyboard_arrow_left</i>}
                onClick={() => {
                  $data.get({ search: { page: parseInt(`${data.payload.page}`) - 1 } })
                }}
                square
              />
            }
            <Button
              baseline={'surface'}
              icon={<i className={classes('material-icons', f18i)}>keyboard_arrow_right</i>}
              size="sm"
              onClick={() => {
                $data.get({ search: { page: data.payload.page + 1 } })
              }}
              square
            />
          </Box>
        </Box>
        <Line dark />
        <Box className={classes(pl12md, pr12md, pl16, pr16, pt16)} flex rounded>
          <Box minHeight={height} fullHeight fullWidth>
            <Box as={<svg />} minHeight={height} fullHeight fullWidth>
              {[...Array(8)].map((_: any, key: any) => {
                const y = (key / 8) * height + 0.5

                return (
                  <line
                    x1={0}
                    y1={y}
                    x2={'100%'}
                    y2={y}
                    stroke={'rgb(238 238 238)'}
                    strokeWidth={1}
                    key={key}
                  />
                )
              })}
              {data.meta.isLoading ? (
                <Text>Loading...</Text>
              ) : (
                <>
                  {payload?.labels?.map((label: any, key: any) => {
                    const x = (key / payload.labels.length) * 100
                    const offsetX = (1 / (payload.labels.length * 2)) * 100

                    return (
                      <g
                        x={`${x}%`}
                        y={0}
                        width={'12.5%'}
                        height={bottomY}
                        key={key}
                        style={{
                          transform: `translateX(${offsetX}%) `,
                        }}
                      >
                        <text
                          x={`${x}%`}
                          y={height - 3 + 0.5}
                          width={'100%'}
                          style={{
                            fontSize: 12,
                            textAlign: 'center',
                            fontWeight: label === 'Today' ? 500 : 400,
                          }}
                          textAnchor="middle"
                        >
                          {label}
                        </text>
                      </g>
                    )
                  })}
                  {Object.keys(toggled)?.map((label: string, index: any) => {
                    const dat = payload.data[index]
                    const offsetX = (1 / (dat.values?.length * 2)) * 100

                    if (!toggled[dat.label]) {
                      return
                    }

                    return (
                      <g
                        key={index}
                        className={style({
                          $nest: {
                            '&:hover': {
                              zIndex: 2,
                            },
                          },
                        })}
                      >
                        {dat.values?.map((item: any, key: any) => {
                          const x = (key / dat.values.length) * 100
                          const y = (key / 8) * height + 0.5
                          return (
                            <rect
                              x={`${x}%`}
                              y={0}
                              width={`${(1 / dat.values.length) * 100}%`}
                              height={bottomEndY}
                              key={key}
                              className={style({
                                fill: 'rgba(0,0,0,0)',
                              })}
                            />
                          )
                        })}
                        {dat.values?.map((item: any, key: any) => {
                          const x = (key / dat.values.length) * 100
                          const y =
                            height - (item.value / payload.largestValue) * (height * 0.75) + 0.5

                          const x2 = ((key + 1) / dat.values.length) * 100
                          const y2 =
                            height -
                            (dat.values[key + 1]?.value / payload.largestValue) * (height * 0.75) +
                            0.5

                          return (
                            <g x={`${x}%`} y={0} width={'12.5%'} height={bottomY} key={key}>
                              {key < dat.values.length - 1 && (
                                <line
                                  x1={`${x}%`}
                                  x2={`${x2}%`}
                                  y1={isNaN(y) ? height : y}
                                  y2={isNaN(y2) ? height : y2}
                                  stroke={COLOR_SCHEMES[index]?.secondary}
                                  strokeWidth={2}
                                  style={{
                                    transform: `translateX(${offsetX}%) translateY(-12.5%)`,
                                  }}
                                />
                              )}

                              <text
                                x={`${x}%`}
                                y={`${isNaN(y) ? height - 7 : y - 7}`}
                                width={'100%'}
                                style={{
                                  fontSize: 13,
                                  textAlign: 'center',
                                  transform: `translateX(${offsetX}%) translateY(-12.5%)`,
                                }}
                                textAnchor="middle"
                              >
                                {item.value}
                              </text>
                            </g>
                          )
                        })}
                      </g>
                    )
                  })}
                </>
              )}
            </Box>
          </Box>
        </Box>
        {/* <Line /> */}
        <Box className={classes(pl12md, pr12md, pl16, pr16, pb12, pt8)} alignRight>
          {payload.data.map((dat: any, key: any) => {
            return (
              <Checkbox
                label={dat.label}
                value={true}
                key={key}
                className={classes(
                  style({
                    $nest: {
                      '.checkbox-button': {
                        maxWidth: '16px !important',
                        minWidth: '16px !important',
                        maxHeight: '16px !important',
                        minHeight: '16px !important',
                      },
                      '.checked': {
                        maxWidth: '16px !important',
                        minWidth: '16px !important',
                        maxHeight: '16px !important',
                        minHeight: '16px !important',
                        background: `${COLOR_SCHEMES[key]?.secondary} !important`,
                      },
                    },
                  }),
                  key !== 0 && ml16,
                )}
                onChange={(value: any) => {
                  const newToggled = { ...toggled }
                  newToggled[dat.label] = value
                  $toggled.set(newToggled)
                }}
              />
            )
          })}
        </Box>
      </Snippet>
    )
  }),
)
